import type { AppDispatch } from '../index';
import { listDaysWithSummaries } from '../../lib/api/content-api-client';
import { getPublicWebsiteConfig } from '../../lib/api/config';
import {
  daysFetchRequested,
  daysFetchSucceeded,
  daysFetchFailed,
} from '../slices/days-slice';

/**
 * Days thunks (task 5.2, public-website spec).
 *
 * Async actions wrapping `listDaysWithSummaries` from the content
 * API client per design §3.1 (Thunks layer). The thunk dispatches
 * the three slice actions in lockstep — `requested` on entry,
 * `succeeded` with the parsed payload on success, `failed` with a
 * string error on rejection — so reducers stay pure and
 * `state.days.statusByPage` tracks the in-flight lifecycle the
 * `<DaysPage>` paginator in 5.5 reads.
 *
 * Page size is forwarded to the API client but is server-pinned to
 * 20 per Property 17; the cache key in the slice is the page
 * index alone.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7.
 */

/** Input shape for `fetchDays`. */
export interface FetchDaysInput {
  /** 1-indexed page number to fetch. */
  readonly page: number;
  /** Page size. Server-pinned to 20 per Property 17. */
  readonly pageSize: number;
}

/**
 * Fetch a page of day summaries and mirror the call into the days
 * slice.
 *
 * @param input The page + pageSize to fetch.
 * @returns A thunk that resolves once the matching slice action has
 *   been dispatched (`succeeded` on 2xx, `failed` on error).
 */
export function fetchDays(input: FetchDaysInput) {
  return async (dispatch: AppDispatch): Promise<void> => {
    dispatch(daysFetchRequested({ page: input.page }));
    try {
      const config = getPublicWebsiteConfig();
      const payload = await listDaysWithSummaries(
        { page: input.page, pageSize: input.pageSize },
        { baseUrl: config.publicApiInvokeUrl, apiKey: config.publicApiKey },
      );
      dispatch(daysFetchSucceeded({ page: input.page, payload }));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      dispatch(daysFetchFailed({ page: input.page, error: message }));
    }
  };
}
